/**
 * CompanyList Component
 * 
 * Displays all companies in a card grid layout with:
 * - Integration status indicators
 * - Quick actions (edit, sync, configure)
 * - Statistics (employees, projects)
 */

import { useState } from 'react';
import { Plus, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CompanyCard } from './CompanyCard';
import { CompanyFormDialog } from './CompanyFormDialog';
import type { CompanyListItem, CompanyStatus } from '@/types';

interface CompanyListProps {
  companies: CompanyListItem[];
  isLoading?: boolean;
  onCompanyClick: (companyId: string) => void;
  onCompanyEdit: (companyId: string) => void;
  onCompanyCreate: () => void;
  onConfigureIntegrations: (companyId: string) => void;
  onRefresh: () => void;
}

export function CompanyList({
  companies,
  isLoading = false,
  onCompanyClick,
  onCompanyEdit,
  onCompanyCreate,
  onConfigureIntegrations,
  onRefresh,
}: CompanyListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<CompanyStatus | 'all'>('all');
  const [integrationFilter, setIntegrationFilter] = useState<'all' | 'connected' | 'not_connected'>('all');

  // Filter companies based on search and filters
  const filteredCompanies = companies.filter((company) => {
    // Search filter
    const matchesSearch =
      searchQuery === '' ||
      company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.code.toLowerCase().includes(searchQuery.toLowerCase());

    // Status filter
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;

    // Integration filter
    const matchesIntegration =
      integrationFilter === 'all' ||
      (integrationFilter === 'connected' &&
        (company.financialIntegrationStatus === 'connected' ||
          company.hrIntegrationStatus === 'connected')) ||
      (integrationFilter === 'not_connected' &&
        company.financialIntegrationStatus !== 'connected' &&
        company.hrIntegrationStatus !== 'connected');

    return matchesSearch && matchesStatus && matchesIntegration;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground mt-1">
            Manage your companies and their integration connections
          </p>
        </div>
        <Button onClick={onCompanyCreate} size="lg">
          <Plus className="h-4 w-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as CompanyStatus | 'all')}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="setup">Setup</SelectItem>
          </SelectContent>
        </Select>
        <Select value={integrationFilter} onValueChange={(value) => setIntegrationFilter(value as 'all' | 'connected' | 'not_connected')}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Integration" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Integrations</SelectItem>
            <SelectItem value="connected">Connected</SelectItem>
            <SelectItem value="not_connected">Not Connected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Company Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-64 bg-muted animate-pulse rounded-lg"
            />
          ))}
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12 bg-muted/50 rounded-lg">
          <p className="text-muted-foreground">
            {searchQuery || statusFilter !== 'all' || integrationFilter !== 'all'
              ? 'No companies match your filters'
              : 'No companies yet'}
          </p>
          {!searchQuery && statusFilter === 'all' && integrationFilter === 'all' && (
            <Button onClick={onCompanyCreate} variant="outline" className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Create your first company
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              onClick={() => onCompanyClick(company.id)}
              onEdit={() => onCompanyEdit(company.id)}
              onConfigureIntegrations={() => onConfigureIntegrations(company.id)}
            />
          ))}
        </div>
      )}

      {/* Summary */}
      <div className="text-sm text-muted-foreground text-center">
        Showing {filteredCompanies.length} of {companies.length} companies
      </div>
    </div>
  );
}
